/**
 * useOptimalTimes Hook
 * 
 * Custom hook for fetching and managing optimal posting times data.
 * Integrates with the backend optimal times API to provide data-driven scheduling suggestions.
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../providers/AuthProvider';
import { createApiService } from '../services/apiUtils';

// Create analytics API service
const analyticsApiService = createApiService('Analytics', '/api/analytics');

/**
 * Custom hook for optimal times functionality
 * @param {string} platform - Platform to get optimal times for (optional)
 * @returns {Object} Optimal times data and functions
 */
export const useOptimalTimes = (platform = null) => {
    const { token } = useAuth();
    const [optimalTimes, setOptimalTimes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [lastFetched, setLastFetched] = useState(null);

    /**
     * Fetch optimal times from the API
     */
    const fetchOptimalTimes = useCallback(async (targetPlatform = platform) => {
        if (!token) {
            setError('Authentication required');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const params = targetPlatform ? { platform: targetPlatform } : {};
            const response = await analyticsApiService.get('/optimal-times/', params);
            
            setOptimalTimes(response.optimal_times || []);
            setLastFetched(new Date().toISOString());
        } catch (err) {
            console.error('Error fetching optimal times:', err);
            setError(err.message || 'Failed to fetch optimal times');
            setOptimalTimes([]);
        } finally {
            setLoading(false);
        }
    }, [platform, token]);

    /**
     * Trigger analysis of optimal times
     */
    const triggerAnalysis = useCallback(async () => {
        if (!token) {
            setError('Authentication required');
            return false;
        }

        try {
            setLoading(true);
            setError(null);

            await analyticsApiService.post('/optimal-times/', {});
            
            // Refresh data after triggering analysis
            await fetchOptimalTimes();
            return true;
        } catch (err) {
            console.error('Error triggering analysis:', err);
            setError(err.message || 'Failed to trigger analysis');
            return false;
        } finally {
            setLoading(false);
        }
    }, [token, fetchOptimalTimes]);

    // Auto-fetch on mount and when dependencies change
    useEffect(() => {
        if (token) {
            fetchOptimalTimes();
        }
    }, [fetchOptimalTimes, token]);

    return {
        optimalTimes,
        loading,
        error,
        lastFetched,
        fetchOptimalTimes,
        triggerAnalysis,
        refetch: fetchOptimalTimes
    };
};

/**
 * Custom hook for checking data sufficiency
 * @param {string} platform - Platform to check data for (optional)
 * @returns {Object} Data sufficiency information
 */
export const useDataSufficiency = (platform = null) => {
    const { token } = useAuth();
    const [dataSufficiency, setDataSufficiency] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    /**
     * Check if user has sufficient data for optimal time analysis
     */
    const checkDataSufficiency = useCallback(async (targetPlatform = platform) => {
        if (!token) {
            setError('Authentication required');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const params = targetPlatform ? { platform: targetPlatform } : {};
            const response = await analyticsApiService.get('/optimal-times/check-data/', params);
            
            setDataSufficiency(response);
        } catch (err) {
            console.error('Error checking data sufficiency:', err);
            setError(err.message || 'Failed to check data sufficiency');
            setDataSufficiency(null);
        } finally {
            setLoading(false);
        }
    }, [platform, token]);

    // Auto-check on mount and when dependencies change
    useEffect(() => {
        if (token) {
            checkDataSufficiency();
        }
    }, [checkDataSufficiency, token]);

    return {
        dataSufficiency,
        loading,
        error,
        checkDataSufficiency,
        refetch: checkDataSufficiency
    };
};

export default useOptimalTimes; 